import React, { useEffect, useState } from "react";
import {
  getPhoneStatus,
  sendPhoneCode,
  verifyPhoneCode,
} from "../api/services/phoneVerificationService";

interface Props {
  /** Current value of the phone field, E.164 or partially typed. */
  phone: string;
  /** Whether this exact number is verified. Lifted so the parent can gate. */
  verified: boolean;
  onVerifiedChange: (verified: boolean) => void;
  /**
   * Called when the API returns a canonical E.164 number, so the parent's
   * field matches what was actually verified.
   */
  onPhoneNormalized?: (phone: string) => void;
  /**
   * Skip the API entirely and accept any 6 digits. For click-through demos
   * with no session (see the renewal test route).
   */
  testMode?: boolean;
}

/**
 * SMS verification for a phone field — send a code, enter it, done.
 *
 * Controlled: the parent owns the number and the verified flag, so it can
 * disable Save until `verified` is true and clear the flag when the field is
 * edited. This component owns only the transient code exchange.
 *
 * On mount it asks the API whether the user's current number is already
 * verified, so a tenant who hasn't changed their number is never asked
 * again. `sendCode` also short-circuits server-side for the same reason.
 */
const PhoneVerification: React.FC<Props> = ({
  phone,
  verified,
  onVerifiedChange,
  onPhoneNormalized,
  testMode = false,
}) => {
  const [codeSent, setCodeSent] = useState(false);
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);

  // Don't ask for a number the tenant has already confirmed.
  useEffect(() => {
    if (testMode || verified || !phone) return;
    let cancelled = false;
    getPhoneStatus()
      .then(({ data }) => {
        if (cancelled || !data.verified || !data.phoneNumber) return;
        if (data.phoneNumber === phone) onVerifiedChange(true);
      })
      .catch(() => {
        /* Non-fatal — fall back to asking for verification. */
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phone, verified, testMode]);

  // A new number invalidates any code already in flight.
  useEffect(() => {
    setCodeSent(false);
    setCode("");
    setError(null);
  }, [phone]);

  const handleSend = async () => {
    setError(null);
    if (testMode) {
      setCodeSent(true);
      return;
    }
    setSending(true);
    try {
      const { data } = await sendPhoneCode(phone);
      if (!data.codeSent && data.verified) {
        // Already verified server-side — nothing sent, nothing to enter.
        onPhoneNormalized?.(data.phoneNumber);
        onVerifiedChange(true);
        return;
      }
      onPhoneNormalized?.(data.phoneNumber);
      setCodeSent(true);
    } catch (err: any) {
      setError(
        err?.response?.data?.message ??
          "We couldn't send a code. Please try again shortly.",
      );
    } finally {
      setSending(false);
    }
  };

  const handleVerify = async () => {
    if (!/^\d{6}$/.test(code)) {
      setError("Enter the 6-digit code we sent you.");
      return;
    }
    setError(null);
    if (testMode) {
      onVerifiedChange(true);
      return;
    }
    setVerifying(true);
    try {
      const { data } = await verifyPhoneCode(phone, code);
      onPhoneNormalized?.(data.phoneNumber);
      onVerifiedChange(data.verified);
    } catch (err: any) {
      setError(
        err?.response?.data?.message ??
          "That code isn't correct. Please try again.",
      );
    } finally {
      setVerifying(false);
    }
  };

  if (verified) {
    return (
      <p className="mt-1.5 text-sm font-medium text-success">
        ✓ Phone number verified
      </p>
    );
  }

  return (
    <div className="mt-2 rounded-lg border border-amber-200 bg-amber-50 p-3">
      {!codeSent ? (
        <>
          <p className="mb-2 text-sm text-gray-700">
            Verify this number to save it.
          </p>
          <button
            type="button"
            onClick={handleSend}
            disabled={!phone.trim() || sending}
            className={`rounded-lg px-4 py-2 text-sm font-medium text-white transition-colors ${
              phone.trim() && !sending
                ? "cursor-pointer bg-warning hover:opacity-90"
                : "cursor-not-allowed bg-gray-300"
            }`}
          >
            {sending ? "Sending…" : "Send Verification Code"}
          </button>
        </>
      ) : (
        <>
          <p className="mb-2 text-sm text-gray-700">
            Enter the 6-digit code sent to {phone}.
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <input
              type="text"
              inputMode="numeric"
              value={code}
              onChange={(e) =>
                setCode(e.target.value.replace(/\D/g, "").slice(0, 6))
              }
              placeholder="123456"
              className="w-32 rounded-lg border border-amber-300 px-3 py-2 text-sm tracking-widest focus:border-primary focus:outline-none"
            />
            <button
              type="button"
              onClick={handleVerify}
              disabled={verifying}
              className={`rounded-lg px-4 py-2 text-sm font-medium text-white transition-colors ${
                verifying
                  ? "cursor-not-allowed bg-gray-300"
                  : "cursor-pointer bg-warning hover:opacity-90"
              }`}
            >
              {verifying ? "Verifying…" : "Verify"}
            </button>
            <button
              type="button"
              onClick={handleSend}
              disabled={sending}
              className="cursor-pointer px-2 text-sm underline disabled:cursor-not-allowed disabled:opacity-50"
            >
              {sending ? "Sending…" : "Resend"}
            </button>
          </div>
        </>
      )}
      {error && <p className="mt-2 text-xs font-medium text-danger">{error}</p>}
    </div>
  );
};

export default PhoneVerification;
